import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Box,
  Stack,
  useTheme,
  TextField,
  InputAdornment,
} from '@mui/material';
import { GetAllResponse } from 'src/services/sites/types';
import { ModalContentRow } from '../modal-content-row/ModalContentRow';
import { CustomWaterLoading } from 'src/components';
import { useAuthentication } from 'src/features/authentication/context';
import { useSiteTags } from 'src/hooks/useSiteTags';
import { TagResponse } from 'src/types/tags/tags';
import { SearchFillIcon } from 'src/assets/icons/SearchFillIcon';
import { useDebounce } from 'src/hooks/useDebounce';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { GatewayInputDataTypeEnum } from 'src/types/gateways';

type ModalContentItemProps = {
  site: GetAllResponse;
  setValue: (name: string, value: string) => void;
  values: any;
  isParticularChart?: boolean;
  disabled?: boolean;
  isTimelineChart?: boolean;
};

export const ModalContentItem = React.memo(
  ({
    site,
    setValue,
    values,
    disabled = false,
    isParticularChart,
    isTimelineChart,
  }: ModalContentItemProps) => {
    const [expanded, setExpanded] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const debouncedValue = useDebounce(searchTerm, 300);

    const {
      customerId: { value: customerId },
    } = useAuthentication();

    const { data: tagsForSpecificSite = [], isLoading } = useSiteTags(
      customerId!,
      site.id,
      expanded,
      debouncedValue
    );

    const theme = useTheme();
    const disabledTextColor = theme.palette.text.disabled;
    const newTags = useMemo(
      () =>
        tagsForSpecificSite.filter(
          (tag: any) => !tag.hidden && tag.dataType !== GatewayInputDataTypeEnum.CALCULATED
        ),
      [tagsForSpecificSite]
    );

    const showTagItem = !isParticularChart || !isTimelineChart;

    useEffect(() => {
      if (isParticularChart) return;
      setValue(`events-${site.id}-${site.siteName}`, 'false');
    }, [site.id, setValue, isParticularChart, site.siteName]);

    const renderModalContentRows = useCallback(
      (data: TagResponse[]) =>
        data?.map((tag) => (
          <ModalContentRow
            values={values}
            key={tag.id}
            tag={tag}
            siteName={site.siteName}
            disabled={disabled}
            setValue={setValue}
            siteId={site?.id}
            isParticularChart
          />
        )),
      [values, site.siteName, disabled, setValue, site?.id]
    );

    return (
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        elevation={0}
        sx={{
          borderRadius: '16px !important',
          overflow: 'visible !important',
          zIndex: expanded ? 50 : 'auto',
          background: '#ffffff',
          '&:before': {
            display: 'none',
          },

          '& .MuiCollapse-wrapperInner': {
            backgroundColor: '#fff',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ArrowForwardIosIcon sx={{ width: 15 }} />}
          sx={{
            backgroundColor: theme.palette.grey[200],
            borderRadius: '16px',
            flexDirection: 'row-reverse',
            '&.Mui-expanded': {
              position: 'sticky',
              top: 0,
              zIndex: 10,
              borderRadius: '16px !important',
              minHeight: 30,
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              marginRight: '5px',
            },
            '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(90deg)',
            },
          }}
          data-sm="advanced-button-input"
        >
          <Typography>{site?.siteName}</Typography>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            mt: 1,
            borderRadius: '32px',
          }}
        >
          <TextField
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
            }}
            name="searchTags"
            size="small"
            fullWidth
            placeholder="Search Tags..."
            inputProps={{ style: { padding: '10px' }, 'data-sm': 'search-input' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchFillIcon fill={disabledTextColor} />
                </InputAdornment>
              ),
            }}
          />
          {isLoading ? (
            <Stack flexDirection={'row'} justifyContent={'center'} p={1}>
              <CustomWaterLoading />
            </Stack>
          ) : (
            <Box>{showTagItem && newTags?.length > 0 ? renderModalContentRows(newTags) : null}</Box>
          )}
          {!isLoading && newTags?.length < 1 ? <Typography>No available tags</Typography> : null}
        </AccordionDetails>
      </Accordion>
    );
  }
);
